import { useMemo } from 'react';

import { PageHeading } from '@/components/PageHeading';
import { getNameLinkCell } from '@/components/Table/LinkCell';
import { TruncatedCell } from '@/components/TruncatedCell';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useLinkToExplorer } from '@/dbt-explorer';
import { useDiscoveryDefinitionSemanticModels } from '@/hooks/discovery/definitionSemanticModels';
import { useUrlProjectParams } from '@/hooks/params';
import { useEnvironmentScope } from '@/hooks/useEnvironmentScope';
import { parseUniqueId } from '@/typings/parseUniqueId';
import { INITIAL_PAGE_SIZE, ResourceFilterTable } from '../ResourceFilterTable';

type TableData = {
  name: string;
  uniqueId: string;
  description: string | null;
  entities: string | undefined;
};

type NamedList = ({ name: string | null } | null)[] | null;

type SemanticModelDefinitionListNode = {
  uniqueId: string;
  description: string | null;
  entities: NamedList;
};

const commaSeparated = (list: NamedList) =>
  list
    ?.flatMap((node) => {
      const name = node?.name;
      return name ? [name] : [];
    })
    .join(', ');

const getTableData = (
  semanticModel: SemanticModelDefinitionListNode | undefined | null,
): TableData | undefined => {
  if (!semanticModel) return undefined;
  const uniqueId = parseUniqueId(semanticModel.uniqueId);
  const entities = commaSeparated(semanticModel.entities);
  return {
    name: uniqueId.remainder,
    uniqueId: uniqueId.raw,
    description: semanticModel.description,
    entities,
  };
};

export const SemanticModelFilterView = () => {
  const pageSize = INITIAL_PAGE_SIZE;
  const { environmentId } = useDbtCloudProject();
  const { userId } = useEnvironmentScope();
  const projectScope = useUrlProjectParams();
  const getDetailsPageLink = useLinkToExplorer('resourceDetails');
  const paginatedSemanticModelQuery = useDiscoveryDefinitionSemanticModels({
    environmentId,
    userId,
    first: pageSize,
  });
  const semanticModels = paginatedSemanticModelQuery.data;
  const pageInfo = paginatedSemanticModelQuery.pageInfo;

  const data = useMemo((): any[] | undefined => {
    return semanticModels?.map(getTableData);
  }, [semanticModels]);

  if (paginatedSemanticModelQuery.error)
    return <>{paginatedSemanticModelQuery.error.stack}</>;
  const isLoadingMore = paginatedSemanticModelQuery.fetching;
  const isInitialLoad =
    paginatedSemanticModelQuery.fetching &&
    paginatedSemanticModelQuery.data === undefined;

  return (
    <>
      <PageHeading className="mb-10">Semantic Models</PageHeading>
      <ResourceFilterTable
        pluralizedResultName="Semantic Models"
        data={data}
        columns={[
          {
            header: 'Name',
            accessorKey: 'name',
            id: 'name',
            size: 60,
            minSize: 240,
            cell: getNameLinkCell(getDetailsPageLink, projectScope),
          },
          {
            header: 'Entities',
            accessorKey: 'entities',
            size: 100,
            minSize: 180,
            cell: TruncatedCell,
          },
          {
            header: 'Description',
            accessorKey: 'description',
            cell: TruncatedCell,
          },
        ]}
        pageInfo={pageInfo}
        isLoading={isInitialLoad}
        isLoadingMore={isLoadingMore}
        pageSize={pageSize}
        fetchMore={paginatedSemanticModelQuery.fetchMore}
      />
    </>
  );
};
